import {
  BarChart3,
  Bell,
  Bot,
  ClipboardList,
  GitBranch,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  MonitorPlay,
  Plug,
  Rocket,
  Settings2,
  Users,
} from "lucide-react";
import { useIsAdmin, useIsManager, useIsMember } from "../lib/permissions";

export type BadgeKey = "attention" | "inProduction" | "inbox" | "waiting";

/** Amber counts ask for someone; the rest are plain totals. */
export const ALERT_BADGES: ReadonlySet<BadgeKey> = new Set(["attention", "inbox", "waiting"]);

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: BadgeKey;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Sits above the Issues group for every role. */
export const NOTIFICATIONS: NavItem = { to: "/inbox", label: "Notifications", icon: Bell, badge: "inbox" };

export const ISSUES_NAV: NavItem = { to: "/issues", label: "Issues", icon: ListChecks, badge: "waiting" };

export const NAV: NavGroup[] = [
  {
    label: "Issues",
    items: [
      ISSUES_NAV,
      { to: "/teams", label: "Teams", icon: Users },
      { to: "/repos", label: "Repos", icon: GitBranch },
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Factory",
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true, badge: "attention" },
      { to: "/tasks", label: "Work orders", icon: ClipboardList, badge: "inProduction" },
      { to: "/releases", label: "Releases", icon: Rocket },
      { to: "/previews", label: "Previews", icon: MonitorPlay },
      { to: "/agents", label: "Agents", icon: Bot },
    ],
  },
  {
    label: "Configure",
    items: [
      { to: "/integrations", label: "Integrations", icon: Plug },
      { to: "/settings", label: "Settings", icon: Settings2 },
    ],
  },
];

/** Users can create issues and open teams, repos and analytics. The factory stays admin only. */
const USER_NAV: NavGroup[] = [
  {
    label: "Issues",
    items: [
      ISSUES_NAV,
      { to: "/teams", label: "Teams", icon: Users },
      { to: "/repos", label: "Repos", icon: GitBranch },
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
];

/** Managers also place issues in releases and look after previews. */
const MANAGER_NAV: NavGroup[] = [
  ...USER_NAV,
  {
    label: "Shipping",
    items: [
      { to: "/releases", label: "Releases", icon: Rocket },
      { to: "/previews", label: "Previews", icon: MonitorPlay },
    ],
  },
];

/** Viewers only see notifications and the issue list. */
const VIEWER_NAV: NavGroup[] = [
  {
    label: "Issues",
    items: [ISSUES_NAV],
  },
];

/** The sidebar's groups for the signed-in role. */
export function useRoleNav(): NavGroup[] {
  const admin = useIsAdmin();
  const manager = useIsManager();
  const member = useIsMember();
  return admin ? NAV : manager ? MANAGER_NAV : member ? USER_NAV : VIEWER_NAV;
}
